import { IconBrandWhatsapp, IconChevronRight, IconMessageCircle, IconPhone, IconSparkles, IconUserHeart } from "@tabler/icons-react";
import { useEffect, useState } from "react";
import { api, ApiError, type Loyalty, type Me, type PaymentsView, type ProposalListItem, type PublicBrand, type TravellersView, type TripSummary } from "../api";
import { ConsentCard } from "../components/ConsentCard";
import { telLink, waLink } from "../components/Footer";
import { InstallBanner } from "../components/InstallBanner";
import { Card, Empty, ErrorNote, SectionTitle, Spinner, StatusChip } from "../components/ui";
import { daysUntil, formatDate, formatDateRange, formatNumber } from "../format";
import { firstNameOf, useSession } from "../session";

export function HomePage() {
  const { me, brand, t } = useSession();
  const [trips, setTrips] = useState<TripSummary[] | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);
  const [linkOnly, setLinkOnly] = useState(false);
  const [tick, setTick] = useState(0);
  const [proposals, setProposals] = useState<ProposalListItem[]>([]);

  useEffect(() => {
    if (!me || me.session.kind !== "full") return;
    api
      .proposals()
      .then((res) => setProposals(res.proposals.filter((p) => p.status !== "accepted" && p.status !== "declined")))
      .catch(() => setProposals([]));
  }, [me, tick]);

  useEffect(() => {
    if (!me) return;
    setError(null);
    api
      .trips()
      .then((res) => setTrips(res.trips))
      .catch((err: unknown) => {
        if (err instanceof ApiError && err.code === "sign_in_required") {
          setLinkOnly(true);
          setTrips([]);
        } else {
          setError(t("somethingWrong"));
          setTrips([]);
        }
      });
  }, [me, tick, t]);

  if (!me || !brand) return null;

  const today = new Date().toISOString().slice(0, 10);
  const active = (trips ?? []).filter((x) => x.status !== "cancelled" && x.status !== "completed" && (!x.endsOn || x.endsOn >= today));
  const sorted = [...active].sort((a, b) => (a.startsOn ?? "9999").localeCompare(b.startsOn ?? "9999"));
  const next = sorted[0];
  const others = sorted.slice(1);
  const past = (trips ?? []).filter((x) => !active.includes(x)).sort((a, b) => (b.startsOn ?? "").localeCompare(a.startsOn ?? ""));

  return (
    <main className="page">
      <h1 className="greeting">{t("greeting", { name: firstNameOf(me) })}</h1>
      <ConsentCard me={me} />
      <InstallBanner />
      {me.session.kind === "link" && (
        <div className="notice">
          <span>{t("linkSessionNote")}</span>
          <a href="/signin" className="button button-small button-primary">
            {t("signIn")}
          </a>
          {me.session.scope?.kind === "trip" && (
            <a href={`/trips/${me.session.scope.subjectId}`} className="button button-small">
              {t("viewTrip")}
            </a>
          )}
        </div>
      )}

      {trips === undefined ? (
        <Spinner />
      ) : error ? (
        <ErrorNote onRetry={() => setTick((n) => n + 1)}>{error}</ErrorNote>
      ) : (
        <>
          {proposals.map((p) => (
            <ProposalCard key={p.id} proposal={p} />
          ))}
          {next ? (
            <>
              <SectionTitle>{t("nextTrip")}</SectionTitle>
              <NextTripCard trip={next} />
            </>
          ) : (
            !linkOnly && proposals.length === 0 && <Empty title={t("noTrips")} body={t("noTripsBody")} />
          )}
          {others.length > 0 && (
            <>
              <SectionTitle>{t("upcoming")}</SectionTitle>
              <div className="stack">
                {others.map((trip) => (
                  <TripRow key={trip.id} trip={trip} />
                ))}
              </div>
            </>
          )}
        </>
      )}

      <LoyaltyCard loyalty={me.loyalty} brand={brand} />
      <ConsultantCard me={me} brand={brand} />

      {past.length > 0 && (
        <>
          <SectionTitle>{t("pastTrips")}</SectionTitle>
          <div className="stack">
            {past.map((trip) => (
              <TripRow key={trip.id} trip={trip} />
            ))}
          </div>
        </>
      )}
    </main>
  );
}

function countdownLabel(trip: TripSummary, t: ReturnType<typeof useSession>["t"]): string | null {
  const days = daysUntil(trip.startsOn);
  if (days === null) return null;
  if (days > 1) return t("inDays", { n: days });
  if (days === 1) return t("tomorrow");
  if (days === 0) return t("today");
  if (trip.endsOn && daysUntil(trip.endsOn)! >= 0) return t("ongoing");
  return null;
}

function ProposalCard({ proposal }: { proposal: ProposalListItem }) {
  const { lang, t } = useSession();
  return (
    <Card href={`/p/${proposal.id}`} className="next-trip proposal-card">
      <div className="next-trip-top">
        <span className="countdown">
          <IconSparkles size={16} /> {t("proposalWaiting")}
        </span>
        <StatusChip status="proposal" lang={lang} />
      </div>
      <h3 className="next-trip-title">{proposal.title}</h3>
      <p className="muted">
        {proposal.startsOn ? formatDateRange(proposal.startsOn, proposal.endsOn, lang) : t("noDate")}
        {proposal.destination ? ` · ${proposal.destination}` : ""}
      </p>
      {proposal.validUntil && <p className="muted small">{t("validUntil", { date: formatDate(proposal.validUntil.slice(0, 10), lang) })}</p>}
      <span className="card-arrow">
        <IconChevronRight size={20} />
      </span>
    </Card>
  );
}

/** The next thing to do on the next trip: pay the deposit, enter traveller details, or the balance date. */
function useNextStep(trip: TripSummary): string | null {
  const { lang, t } = useSession();
  const [payments, setPayments] = useState<PaymentsView | null | undefined>(undefined);
  const [travellers, setTravellers] = useState<TravellersView | undefined>(undefined);
  const managed = trip.managed ?? trip.source !== "legacy";
  useEffect(() => {
    if (!managed) return;
    api.payments(trip.id).then((res) => setPayments(res.payments)).catch(() => setPayments(null));
    api.travellers(trip.id).then(setTravellers).catch(() => undefined);
  }, [trip.id, managed]);
  if (!managed) return null;
  if (payments?.next?.kind === "deposit") return t("payDeposit");
  if (travellers?.travellers.some((x) => x.status === "missing" || x.status === "rejected")) return t("enterTravellers");
  if (payments && (payments.plan.status === "deposit_paid" || payments.plan.status === "balance_due" || payments.plan.status === "overdue") && payments.totals.outstanding > 0) {
    return t("balanceDueHint", { date: formatDate(payments.next?.dueOn ?? payments.plan.balanceDueOn, lang) });
  }
  return null;
}

function NextTripCard({ trip }: { trip: TripSummary }) {
  const { lang, t } = useSession();
  const countdown = countdownLabel(trip, t);
  const nextStep = useNextStep(trip);
  return (
    <Card href={`/trips/${trip.id}`} className="next-trip">
      <div className="next-trip-top">
        {countdown && <span className="countdown">{countdown}</span>}
        <StatusChip status={trip.status} lang={lang} />
      </div>
      <h3 className="next-trip-title">{trip.title}</h3>
      <p className="muted">
        {trip.startsOn ? formatDateRange(trip.startsOn, trip.endsOn, lang) : t("noDate")}
        {trip.destination ? ` · ${trip.destination}` : ""}
      </p>
      {nextStep ? (
        <p className="hint">
          <IconSparkles size={16} /> {nextStep}
        </p>
      ) : (
        trip.status === "proposal" && (
          <p className="hint">
            <IconSparkles size={16} /> {t("proposalWaiting")}
          </p>
        )
      )}
      <span className="card-arrow">
        <IconChevronRight size={20} />
      </span>
    </Card>
  );
}

function TripRow({ trip }: { trip: TripSummary }) {
  const { lang, t } = useSession();
  return (
    <Card href={`/trips/${trip.id}`} className="trip-row">
      <div className="trip-row-text">
        <strong>{trip.title}</strong>
        <span className="muted small">{trip.startsOn ? formatDateRange(trip.startsOn, trip.endsOn, lang) : t("noDate")}</span>
      </div>
      <StatusChip status={trip.status} lang={lang} />
      <IconChevronRight size={18} className="muted" />
    </Card>
  );
}

function tierName(tier: Loyalty["tier"]): string | null {
  if (!tier) return null;
  if (typeof tier === "string") return tier;
  return tier.name ?? null;
}

export function LoyaltyCard({ loyalty, brand }: { loyalty: Loyalty | null; brand: PublicBrand }) {
  const { lang, t } = useSession();
  if (!loyalty) {
    return (
      <Card href="/club" className="loyalty-card loyalty-join">
        <span className="eyebrow">{brand.loyaltyProgramme}</span>
        <h3>{t("joinClub", { programme: brand.loyaltyProgramme })}</h3>
        <p className="muted small">{t("joinClubBody")}</p>
        <span className="card-arrow">
          <IconChevronRight size={20} />
        </span>
      </Card>
    );
  }
  const points = (loyalty.balances ?? []).reduce((sum, b) => sum + (typeof b.points === "number" ? b.points : 0), 0);
  const tier = tierName(loyalty.tier);
  return (
    <Card href="/club" className="loyalty-card">
      <span className="eyebrow">{brand.loyaltyProgramme}</span>
      <div className="loyalty-row">
        <div>
          <span className="loyalty-points">{formatNumber(points, lang)}</span>
          <span className="muted"> {t("points")}</span>
        </div>
        {tier && (
          <div className="loyalty-tier">
            <span className="muted small">{t("tier")}</span>
            <strong>{tier}</strong>
          </div>
        )}
      </div>
      <p className="small loyalty-more">
        {t("viewClub")} <IconChevronRight size={16} />
      </p>
    </Card>
  );
}

function initials(name: string) {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? "")
    .join("");
}

/** The consultant's card (docs/16 §2.3): name and languages are public; the contacts are the brand's channels, never a personal number. */
export function ConsultantCard({ me, brand }: { me: Me; brand: PublicBrand }) {
  const { t } = useSession();
  const consultant = me.consultant;
  const assigned = !!me.contact?.consultantId;
  return (
    <Card className="consultant-card">
      <div className="consultant-head">
        {consultant ? (
          <span className="avatar avatar-initials">{initials(consultant.name)}</span>
        ) : (
          <span className="avatar">
            <IconUserHeart size={24} stroke={1.5} />
          </span>
        )}
        <div>
          <span className="eyebrow">{t("yourConsultant")}</span>
          {consultant ? (
            <>
              <h3>{consultant.name}</h3>
              {consultant.languages.length > 0 && <p className="muted small">{t("speaks", { langs: consultant.languages.map((l) => l.toUpperCase()).join(", ") })}</p>}
            </>
          ) : (
            <p className="small">{assigned ? t("consultantAssigned") : t("consultantNotYet")}</p>
          )}
        </div>
      </div>
      <div className="channels channels-compact">
        <a href="/conversation">
          <IconMessageCircle size={18} stroke={1.75} />
          <span>{t("writeToMe")}</span>
        </a>
        <a href={waLink(brand.channels.whatsapp)} target="_blank" rel="external noreferrer">
          <IconBrandWhatsapp size={18} stroke={1.75} />
          <span>{t("whatsapp")}</span>
        </a>
        <a href={telLink(brand.channels.phone)} rel="external">
          <IconPhone size={18} stroke={1.75} />
          <span>{t("call")}</span>
        </a>
      </div>
    </Card>
  );
}
